import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  House, Calendar, ChartBar, ChatCircleText, Notebook,
  CreditCard, PlugsConnected, SignOut, GraduationCap, FolderOpen, VideoCamera, List, X, ShieldCheck, ChalkboardTeacher, UsersThree, Compass,
} from "@phosphor-icons/react";
import AIAssistantButton from "@/components/AIAssistantButton";

const NAV = {
  admin: [
    { to: "/admin", icon: House, label: "Visão Geral" },
    { to: "/teachers", icon: ChalkboardTeacher, label: "Professores" },
    { to: "/students-manage", icon: UsersThree, label: "Alunos & Pais" },
    { to: "/payments", icon: CreditCard, label: "Pagamentos" },
    { to: "/agenda", icon: Calendar, label: "Atividades" },
    { to: "/tutoring", icon: VideoCamera, label: "Tutoria & Meet" },
    { to: "/performance", icon: ChartBar, label: "Desempenho" },
    { to: "/materials", icon: FolderOpen, label: "Materiais" },
    { to: "/tutor", icon: ChatCircleText, label: "Tutor IA" },
    { to: "/plan", icon: Notebook, label: "Planos IA" },
    { to: "/anamnesis", icon: Compass, label: "Perfil de Estudos" },
    { to: "/webhooks", icon: PlugsConnected, label: "N8N Webhooks" },
  ],
  teacher: [
    { to: "/teacher", icon: House, label: "Meu Painel" },
    { to: "/students-manage", icon: UsersThree, label: "Meus Alunos" },
    { to: "/payments", icon: CreditCard, label: "Mensalidades" },
    { to: "/agenda", icon: Calendar, label: "Atividades" },
    { to: "/tutoring", icon: VideoCamera, label: "Tutoria & Meet" },
    { to: "/performance", icon: ChartBar, label: "Desempenho" },
    { to: "/materials", icon: FolderOpen, label: "Materiais" },
    { to: "/lessons", icon: VideoCamera, label: "Minhas Aulas" },
    { to: "/tutor", icon: ChatCircleText, label: "Tutor IA" },
    { to: "/plan", icon: Notebook, label: "Planos IA" },
    { to: "/anamnesis", icon: Compass, label: "Perfil de Estudos" },
  ],
  student: [
    { to: "/student", icon: House, label: "Meu Painel" },
    { to: "/agenda", icon: Calendar, label: "Atividades" },
    { to: "/tutoring", icon: VideoCamera, label: "Sessões 1:1" },
    { to: "/performance", icon: ChartBar, label: "Desempenho" },
    { to: "/materials", icon: FolderOpen, label: "Materiais" },
    { to: "/lessons", icon: VideoCamera, label: "Aulas Disponíveis" },
    { to: "/tutor", icon: ChatCircleText, label: "Tutor IA" },
    { to: "/plan", icon: Notebook, label: "Plano de Estudos" },
    { to: "/anamnesis", icon: Compass, label: "Meu Perfil de Estudos" },
  ],
  parent: [
    { to: "/parent", icon: House, label: "Acompanhamento" },
    { to: "/agenda", icon: Calendar, label: "Atividades" },
    { to: "/tutoring", icon: VideoCamera, label: "Sessões 1:1" },
    { to: "/performance", icon: ChartBar, label: "Desempenho" },
    { to: "/materials", icon: FolderOpen, label: "Materiais" },
    { to: "/payments", icon: CreditCard, label: "Pagamentos" },
  ],
};

const ROLE_LABEL = { admin: "Super Administrador", teacher: "Professor", student: "Aluno(a)", parent: "Responsável" };

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map(s => s[0]).join("").toUpperCase();
}

function SidebarInner({ items, user, onLogout, onClose }) {
  return (
    <>
      <div className="card-soft p-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl gradient-primary flex items-center justify-center shadow-md flex-shrink-0">
          <GraduationCap size={22} color="white" weight="duotone" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading font-black text-base leading-tight">Caminhos</div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">APP</div>
        </div>
        {onClose && (
          <button onClick={onClose} data-testid="close-drawer" className="lg:hidden w-9 h-9 rounded-xl hover:bg-zinc-100 flex items-center justify-center">
            <X size={18} />
          </button>
        )}
      </div>

      <nav className="mt-4 flex-1 card-soft p-3 flex flex-col gap-1 overflow-y-auto">
        {items.map(({ to, icon: Icon, label }) => (
          <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}`}
            onClick={onClose}
            className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}>
            <Icon size={20} weight="duotone" />
            <span className="flex-1">{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="mt-4 card-soft p-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full gradient-warm flex items-center justify-center flex-shrink-0">
            <span className="text-white font-heading font-black text-sm">{initials(user?.name)}</span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold truncate">{user?.name}</div>
            <div className="text-xs text-zinc-500 truncate">{ROLE_LABEL[user?.role]}</div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onLogout} data-testid="logout-btn"
                className="w-full mt-3 rounded-2xl border-zinc-200 hover:bg-zinc-50">
          <SignOut size={14} className="mr-2" /> Sair
        </Button>
        <NavLink to="/data-rights" data-testid="nav-data-rights" onClick={onClose}
                 className="mt-2 flex items-center justify-center gap-1 text-[11px] font-semibold text-zinc-500 hover:text-[#1E5FFF]">
          <ShieldCheck size={12} weight="duotone" /> Meus dados • LGPD
        </NavLink>
      </div>
    </>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  let items = NAV[user?.role] || [];
  // Aluno com acesso não liberado: sidebar mostra apenas a anamnese
  if (user?.role === "student" && user?.access_unlocked === false) {
    items = items.filter(i => i.to === "/anamnesis");
  }
  const [drawerOpen, setDrawerOpen] = useState(false);

  async function handleLogout() { await logout(); nav("/login"); }

  return (
    <div className="min-h-screen flex bg-[#F7F3EE]">
      <aside className="hidden lg:flex w-72 flex-col p-4 sticky top-0 h-screen flex-shrink-0">
        <SidebarInner items={items} user={user} onLogout={handleLogout} />
      </aside>

      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-[#F7F3EE]/95 backdrop-blur border-b border-zinc-200/50 px-4 py-3 flex items-center gap-3">
        <button data-testid="open-drawer" onClick={() => setDrawerOpen(true)}
                className="w-10 h-10 rounded-xl bg-white shadow-sm border border-zinc-200 flex items-center justify-center">
          <List size={20} />
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-xl gradient-primary flex items-center justify-center flex-shrink-0">
            <GraduationCap size={18} color="white" weight="duotone" />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-black text-sm leading-none">Caminhos</div>
            <div className="text-[9px] uppercase tracking-widest font-bold text-zinc-500">APP</div>
          </div>
        </div>
        <div className="w-9 h-9 rounded-full gradient-warm flex items-center justify-center flex-shrink-0">
          <span className="text-white font-heading font-black text-xs">{initials(user?.name)}</span>
        </div>
      </div>

      {drawerOpen && (
        <>
          <div onClick={() => setDrawerOpen(false)} className="lg:hidden fixed inset-0 bg-black/40 z-40" />
          <aside className="lg:hidden fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-[#F7F3EE] z-50 p-4 flex flex-col overflow-y-auto shadow-2xl">
            <SidebarInner items={items} user={user} onLogout={handleLogout} onClose={() => setDrawerOpen(false)} />
          </aside>
        </>
      )}

      <main className="flex-1 overflow-auto min-w-0 pt-16 lg:pt-0">{children}</main>
      <AIAssistantButton />
    </div>
  );
}
